import format from 'date-fns/format'
import { FC } from 'react'
import { useNavigate } from 'react-router-dom'

import s from './CampaignInfoPopup.module.scss'

import { Campaign } from 'types/campaign'
import { StatisticsToInfoCard } from 'types/stats'

import { STATUS_NAME_BY_STATUS_VALUE, STATUS_STYLE_BY_STATUS_VALUE } from 'constants/campaignStatusConsts'

import transformUTCToLocale from 'utils/transformUTCToLocale'

import { useShowDropdown } from 'hooks/useShowDropdown'

import { AdSpaceIcon } from 'assets/icons/AdSpaceIcon'
import { AudienceIcon } from 'assets/icons/AudienceIcon'
import { ClockIcon } from 'assets/icons/ClockIcon'

import { CustomButton } from 'components/UI/CustomButton/CustomButton'
import { CustomSlider } from 'components/UI/CustomSlider/CustomSlider'
import { Dropmenu } from 'components/UI/Dropmenu/Dropmenu'
import { ModalFullScreen } from 'components/UI/ModalFullScreen/ModalFullScreen'
import { Status } from 'components/UI/Status/Status'
import { useDeviceContext } from 'contexts/DeviceContext'
import { Intervals } from 'utils/createIntervalFromString'

interface CampaignInfoPopupProps {
  campaign: Campaign | null
  onCloseModal: () => void
  imagesToSlider: string[]
  status: boolean
  statistics: StatisticsToInfoCard | null
  changeIntervalHandler: (period: Intervals) => void
  period: Intervals
}

const CampaignInfoPopupView: FC<CampaignInfoPopupProps> = (props) => {
  const { campaign, onCloseModal, imagesToSlider, status, changeIntervalHandler, statistics, period } = props
  const {deviceWidth} = useDeviceContext()
  const { ref, showDropdown } = useShowDropdown()
  const navigate = useNavigate()

  const editCampaignHandler = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    e.nativeEvent.stopImmediatePropagation()
    navigate(`../edit/${campaign?.id}`)
  }

  return (
    <ModalFullScreen onHide={onCloseModal} loading={status} className={s.modalWrapper}>
      <div></div>
      <div className={s.wrapper}>
        <CustomSlider
          images={imagesToSlider}
          itemStyle={deviceWidth > 600 ? { height: '21.125rem', width: '37.5rem' }: { height: '21.125rem', width: '100%', maxWidth: '34.5rem' }}
          style={deviceWidth > 600 ? { height: '21.125rem', width: '37.5rem' }: { height: '21.125rem', width: '100%', maxWidth: '34.5rem' }}
        />
        <div className={s.info}>
          <div className={s.nameAndStatus}>
            <h3 className={s.name}>{campaign?.name}</h3>
            {campaign !== null ? (
              <Status
                label={STATUS_NAME_BY_STATUS_VALUE[campaign.status]}
                color={STATUS_STYLE_BY_STATUS_VALUE[campaign.status]}
              />
            ) : null}
          </div>
          <div className={s.description}>
            <p>{campaign?.description}</p>
          </div>
          <div className={s.gridContent}>
            <div className={s.gridContent__item}>
              <p className={s.label}>Starts at</p>
              <p className={s.text}>
                {campaign?.start_datetime
                  ? format(transformUTCToLocale(campaign?.start_datetime), 'MMM dd yyyy HH:mm')
                  : null}
              </p>
            </div>
            <div className={s.gridContent__item}>
              <p className={s.label}>Ends at</p>
              <p className={s.text}>
                {campaign?.finish_datetime
                  ? format(transformUTCToLocale(campaign?.finish_datetime), 'MMM dd yyyy HH:mm')
                  : null}
              </p>
              <ClockIcon className={s.clockIcon} />
            </div>
          </div>
          <div className={s.audienceAndAdspots}>
            <div className={s.audienceInfo}>
              <AudienceIcon className={s.infoIcon} />
              <span className={s.audienceInfoText}>{campaign?.audience?.name}</span>
            </div>
            <div className={s.adSpotInfo}>
              <AdSpaceIcon className={s.infoIcon} />
              {campaign?.ad_campaign_places?.length} Ad Spaces
            </div>
          </div>
          <div className={s.footer}>
            <div className={s.footerItem}>
              <h4>{statistics?.day || 0}</h4>
              <span>Views today</span>
            </div>
            <div className={s.footerItem}>
              <h4>{statistics?.interval || 0}</h4>
              <Dropmenu
                variant="transparent"
                show={showDropdown}
                title={<span className={s.bottomText}>{`This ${period}`}</span>}
                cref={ref}
                items={[
                  { title: 'This Year', onClick: () => changeIntervalHandler('year') },
                  { title: 'This Month', onClick: () => changeIntervalHandler('month') },
                  { title: 'This Week', onClick: () => changeIntervalHandler('week') },
                  { title: 'This Day', onClick: () => changeIntervalHandler('day') },
                  { title: 'This Hour', onClick: () => changeIntervalHandler('hour') },
                ]}
              />
            </div>
            <div className={s.footerItem}>
              <h4>{statistics?.total || 0}</h4>
              <span>Total views</span>
            </div>
            <span onClick={() => navigate('info')} className={s.moreLink}>
              More
            </span>
          </div>
        </div>
      </div>
      <div className={s.buttonsWrapper}>
        {campaign?.status === 0 ? (
          <CustomButton variant="primary" onClick={editCampaignHandler} size="240" className={s.editBtn}>
            Edit
          </CustomButton>
        ) : null}
        <CustomButton variant="outlined" onClick={onCloseModal} size="152">
          Close
        </CustomButton>
      </div>
    </ModalFullScreen>
  )
}

export default CampaignInfoPopupView
